/**
 * Screenshot capture zone. Listens for paste events on the form, accepts
 * drag-and-drop, and exposes a file-picker fallback. Each accepted image
 * is uploaded to Convex `_storage` immediately so submit doesn't have to
 * wait on uploads.
 *
 * Storage flow (mirrors the iconLibrary pattern):
 *   1. Call generateScreenshotUploadUrl mutation → signed POST URL
 *   2. fetch(url, {method: POST, body: blob}) → returns {storageId}
 *   3. Stash storageId in local state; preview from object-URL on the blob
 *
 * On submit, the parent form passes `screenshotStorageIds` to the submit
 * mutation. If the user closes the form mid-upload, the uploaded files
 * remain in `_storage` orphaned — beta scale, low cost; a sweep job can
 * GC orphans later if needed.
 *
 * Multi-image: paste/drop multiple, each shows a thumbnail with a remove
 * button. Order is upload order (preserved by `order` index on the row).
 *
 * Demo mode: parent component hides the form so this never renders without
 * auth. The upload-url mutation throws "not authenticated" otherwise.
 */

import React from 'react';
import { useGenerateScreenshotUploadUrl } from '../hooks/useBugReports.js';

export default function ScreenshotPasteZone({ items, onChange }) {
  const generateUploadUrl = useGenerateScreenshotUploadUrl();
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(0);
  const [error, setError] = React.useState(null);

  const handleBlobs = React.useCallback(async (blobs) => {
    if (!blobs || blobs.length === 0) return;
    setError(null);
    setUploading(n => n + blobs.length);
    try {
      for (const blob of blobs) {
        try {
          const url = await generateUploadUrl();
          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': blob.type || 'image/png' },
            body: blob,
          });
          if (!res.ok) throw new Error(`upload failed: ${res.status}`);
          const { storageId } = await res.json();
          // previewUrl is a local object URL — small (browser caches the
          // blob), revoked when the item is removed or component unmounts.
          const previewUrl = URL.createObjectURL(blob);
          onChange(prev => ([
            ...(prev || []),
            { storageId, previewUrl, mime: blob.type || 'image/png', size: blob.size },
          ]));
        } finally {
          setUploading(n => Math.max(0, n - 1));
        }
      }
    } catch (e) {
      console.error('Screenshot upload failed:', e);
      setError(e.message || 'Upload failed');
      setUploading(0);
    }
  }, [generateUploadUrl, onChange]);

  // Paste listener (window-scoped — paste anywhere in the form drops the
  // image here). The form's keydown handlers don't intercept paste, and
  // attaching to the form root sometimes misses paste events when a child
  // input has focus. Window-level catches them all.
  React.useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageBlobs = [];
      for (const item of items) {
        if (item.type && item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) imageBlobs.push(blob);
        }
      }
      if (imageBlobs.length === 0) return;
      e.preventDefault();
      handleBlobs(imageBlobs);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [handleBlobs]);

  const onDrop = (e) => {
    e.preventDefault();
    const blobs = Array.from(e.dataTransfer?.files || []).filter(f => (f.type || '').startsWith('image/'));
    if (blobs.length) handleBlobs(blobs);
  };

  const onFilePicker = (e) => {
    const blobs = Array.from(e.target.files || []).filter(f => (f.type || '').startsWith('image/'));
    if (blobs.length) handleBlobs(blobs);
    e.target.value = '';
  };

  const removeAt = (idx) => {
    onChange(prev => {
      const next = (prev || []).slice();
      const removed = next.splice(idx, 1)[0];
      if (removed?.previewUrl) {
        try { URL.revokeObjectURL(removed.previewUrl); } catch (_) { /* noop */ }
      }
      return next;
    });
  };

  // Cleanup object URLs on unmount.
  React.useEffect(() => () => {
    for (const it of (items || [])) {
      if (it.previewUrl) {
        try { URL.revokeObjectURL(it.previewUrl); } catch (_) { /* noop */ }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      style={{
        border: '1px dashed rgba(201,162,39,0.4)',
        borderRadius: 8,
        padding: 10,
        background: 'rgba(0,0,0,0.18)',
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: '0.7rem', color: '#c9a227' }}>
          📸 Paste, drop, or pick a screenshot
        </span>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={{
            padding: '3px 8px',
            fontSize: '0.65rem',
            background: 'rgba(201,162,39,0.12)',
            border: '1px solid rgba(201,162,39,0.4)',
            borderRadius: 4,
            color: '#c9a227',
            cursor: 'pointer',
          }}
        >
          Choose files…
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onFilePicker}
          style={{ display: 'none' }}
        />
      </div>

      <div style={{ fontSize: '0.6rem', color: '#888', marginBottom: 6 }}>
        Tip: Snipping Tool / Win+Shift+S will paste here directly with Ctrl+V.
      </div>

      {error && (
        <div style={{ fontSize: '0.65rem', color: '#ff6961', marginBottom: 6 }}>
          {error}
        </div>
      )}

      {(items && items.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {items.map((it, idx) => (
            <div
              key={`${it.storageId}-${idx}`}
              style={{
                position: 'relative',
                width: 84,
                height: 84,
                borderRadius: 4,
                overflow: 'hidden',
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <img
                src={it.previewUrl}
                alt={`Screenshot ${idx + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <button
                type="button"
                onClick={() => removeAt(idx)}
                title="Remove"
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {uploading > 0 && (
        <div style={{ fontSize: '0.65rem', color: '#c9a227', marginTop: 6 }}>
          Uploading {uploading} image{uploading > 1 ? 's' : ''}…
        </div>
      )}
    </div>
  );
}
